import { apiClient } from '@/app/core/services/apiClient.service';
import { BaseApiService } from '@/app/shared/services/apis/baseApi.service';
import type { ApiResponse } from '@/app/shared/services/apis/types/ApiResponse';
import type {
    EventDetails,
    EventListItem,
    EventStage,
} from '@/app/shared/services/apis/types/Events';
import type { ID } from '@fifa-bsm/bo-frontend-shared/utils/types';

class EventsApiService extends BaseApiService {
    getList(
        mainEventId: ID,
        page: number = 1,
        perPage: number = 1000,
        params: Record<string, any> = {}
    ): Promise<ApiResponse<EventListItem[]>> {
        return this.client.get(`main-events/${mainEventId}/events`, {
            page,
            count: perPage,
            ...params,
        });
    }

    getById(mainEventId: ID, eventId: ID): Promise<ApiResponse<EventDetails>> {
        return this.client.get(`main-events/${mainEventId}/events/${eventId}`);
    }

    changeUsersRolesForEvent(eventId: ID, payload: any): Promise<ApiResponse<any>> {
        return this.client.put(`events/${eventId}/users/attach`, payload);
    }

    changeUsersRolesForMainEvent(mainEventId: ID, payload: any): Promise<ApiResponse<any>> {
        return this.client.put(`main-events/${mainEventId}/users/attach`, payload);
    }

    create(mainEventId: ID, data: any): Promise<ApiResponse<any>> {
        return this.client.post(`main-events/${mainEventId}/events`, data);
    }

    update(mainEventId: ID, eventId: ID, data: any): Promise<ApiResponse<any>> {
        return this.client.put(`main-events/${mainEventId}/events/${eventId}`, data);
    }

    getGuestOperationsBookingConfiguration(
        mainEventId: ID,
        eventId: ID,
        bookingId: ID
    ): Promise<ApiResponse<any>> {
        return this.client.get(
            `main-events/${mainEventId}/events/${eventId}/go-booking-configurations/${bookingId}`
        );
    }

    createGuestOperationsBookingConfiguration(
        mainEventId: ID,
        eventId: ID,
        data: any
    ): Promise<ApiResponse<any>> {
        return this.client.post(
            `main-events/${mainEventId}/events/${eventId}/go-booking-configurations`,
            data
        );
    }

    updateGuestOperationsBookingConfiguration(
        mainEventId: ID,
        eventId: ID,
        bookingId: ID,
        data: any
    ): Promise<ApiResponse<any>> {
        return this.client.put(
            `main-events/${mainEventId}/events/${eventId}/go-booking-configurations/${bookingId}`,
            data
        );
    }

    getStages(): Promise<ApiResponse<EventStage[]>> {
        return this.client.get(`event-stages`);
    }

    async sendTestNotificationEmail(
        mainEventId: ID,
        notificationConfigurationId: ID,
        payload: any
    ): Promise<ApiResponse<any>> {
        return this.client.post(
            `main-events/${mainEventId}/notification-configurations/${notificationConfigurationId}/send-test`,
            payload
        );
    }

    getSeatingSettings(mainEventId: ID, eventId: ID): Promise<ApiResponse<any>> {
        return this.client.get(`main-events/${mainEventId}/events/${eventId}/seating-settings`);
    }

    updateSeatingSettings(mainEventId: ID, eventId: ID, payload: any): Promise<ApiResponse<any>> {
        return this.client.patch(
            `main-events/${mainEventId}/events/${eventId}/seating-settings`,
            payload
        );
    }
}

export const eventsApi = new EventsApiService(apiClient);
